import React, { memo, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, Search, X, Info, ChevronDown } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';
import { BusinessInfoSection, OwnerInfoSection, ParticipantsSection } from './DealFormSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Typeahead combobox — type to filter, click to select */
const SearchableCombobox = memo(({ value, options, onChange, placeholder, getLabel, getId, renderOption }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = options.find(o => getId(o) === value);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => getLabel(o).toLowerCase().includes(q));
  }, [search, options, getLabel]);

  const handleSelect = (opt) => {
    onChange(getId(opt));
    setSearch('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div
        className={`flex items-center border rounded-md bg-white overflow-hidden transition-all ${open ? 'ring-2 ring-ring ring-offset-0' : ''}`}
        onClick={() => { setOpen(true); }}
      >
        {open || !selected ? (
          <input
            className="flex-1 h-10 px-3 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            value={open ? search : ''}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={selected && !open ? getLabel(selected) : placeholder}
            autoComplete="off"
            data-testid="combobox-input"
          />
        ) : (
          <span className="flex-1 h-10 flex items-center px-3 text-sm cursor-default select-none">
            {getLabel(selected)}
          </span>
        )}
        <div className="flex items-center pr-2 gap-1">
          {value && (
            <button type="button" className="text-muted-foreground hover:text-foreground p-0.5 rounded" onClick={handleClear} tabIndex={-1}>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">No results</div>
          ) : (
            filtered.map(opt => (
              <div
                key={getId(opt)}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-accent ${getId(opt) === value ? 'bg-accent font-medium' : ''}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(opt)}
              >
                {renderOption ? renderOption(opt) : getLabel(opt)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
});

const DealForm = memo(({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  error,
  editingDeal,
  brokers,
  merchants,
  funders,
  handleFieldChange,
  allDeals,
  getAuthHeaders,
}) => {
  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantDropdownOpen, setMerchantDropdownOpen] = useState(false);
  const [refiDealSearch, setRefiDealSearch] = useState('');
  const [refiDropdownOpen, setRefiDropdownOpen] = useState(false);
  const [refiDealDetail, setRefiDealDetail] = useState(null);
  const [refiLoading, setRefiLoading] = useState(false);
  const merchantRef = useRef(null);
  const refiRef = useRef(null);

  const getBrokerLabel = useCallback((b) => {
    const tag = (b.contact_types || []).includes('ISO') ? '(ISO)' : '(Broker)';
    return `${b.company_name} ${tag}`;
  }, []);
  const getFunderLabel = useCallback((f) => f.company_name || '', []);

  // Fetch refi deal details when a deal is selected
  useEffect(() => {
    if (!formData.refi_from_deal || !getAuthHeaders) {
      setRefiDealDetail(null);
      return;
    }
    let cancelled = false;
    setRefiLoading(true);
    // Find deal_id number from allDeals to use the detail endpoint
    const selectedDeal = (allDeals || []).find(d => d.id === formData.refi_from_deal);
    const dealIdentifier = selectedDeal ? selectedDeal.deal_id : formData.refi_from_deal;
    axios.get(`${API}/deals/${dealIdentifier}`, { headers: getAuthHeaders() })
      .then(res => {
        if (!cancelled) {
          setRefiDealDetail(res.data);
          // Auto-set payoff amount to effective balance
          const effectiveBalance = res.data.balance_with_fees ?? res.data.balance ?? 0;
          handleChange('refi_balance_payoff', effectiveBalance.toString());
        }
      })
      .catch(() => { if (!cancelled) setRefiDealDetail(null); })
      .finally(() => { if (!cancelled) setRefiLoading(false); });
    return () => { cancelled = true; };
  }, [formData.refi_from_deal]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (merchantRef.current && !merchantRef.current.contains(e.target)) setMerchantDropdownOpen(false);
      if (refiRef.current && !refiRef.current.contains(e.target)) setRefiDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedMerchant = merchants.find(m => m.id === formData.company_id);
  const filteredMerchants = useMemo(() => merchantSearch.length > 0
    ? merchants.filter(m => 
        (m.company_name || '').toLowerCase().includes(merchantSearch.toLowerCase()) ||
        (m.shortcode || '').toLowerCase().includes(merchantSearch.toLowerCase())
      )
    : merchants, [merchantSearch, merchants]);

  const filteredRefiDeals = useMemo(() => (allDeals || []).filter(d => {
    if (!refiDealSearch) return true;
    const q = refiDealSearch.toLowerCase();
    return String(d.deal_id || '').includes(q) ||
      (d.deal_name || '').toLowerCase().includes(q) ||
      (d.merchant_name || '').toLowerCase().includes(q);
  }).slice(0, 20), [refiDealSearch, allDeals]);

  const handleChange = (field, value) => {
    if (handleFieldChange) {
      handleFieldChange(field, value);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Tabs defaultValue="core-terms" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="core-terms">Core Terms</TabsTrigger>
          <TabsTrigger value="participants">Participants</TabsTrigger>
          <TabsTrigger value="business-info">Business Information</TabsTrigger>
          <TabsTrigger value="owner-info">Owner Information</TabsTrigger>
        </TabsList>

        {/* Tab 1: Core Terms */}
        <TabsContent value="core-terms" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_id" className="flex items-center">
                Merchant <span className="text-red-500 ml-1">*</span>
                <span className="text-xs text-gray-500 ml-2">(Required)</span>
              </Label>
              <div ref={merchantRef} className="relative">
                {selectedMerchant && !merchantDropdownOpen ? (
                  <div 
                    className="flex items-center justify-between border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                    onClick={() => { setMerchantDropdownOpen(true); setMerchantSearch(''); }}
                    data-testid="merchant-selected"
                  >
                    <span>{selectedMerchant.company_name} {selectedMerchant.shortcode ? `(${selectedMerchant.shortcode})` : ''}</span>
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600" onClick={(e) => { e.stopPropagation(); handleChange('company_id', ''); }} />
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      data-testid="merchant-search-input"
                      placeholder="Type to search merchants..."
                      value={merchantSearch}
                      onChange={(e) => { setMerchantSearch(e.target.value); setMerchantDropdownOpen(true); }}
                      onFocus={() => setMerchantDropdownOpen(true)}
                      className={`pl-9 ${!formData.company_id ? 'border-orange-300' : ''}`}
                      autoComplete="off"
                    />
                  </div>
                )}
                {merchantDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredMerchants.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">No merchants found</div>
                    ) : (
                      filteredMerchants.slice(0, 30).map(m => (
                        <div
                          key={m.id}
                          className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50"
                          onClick={() => {
                            handleChange('company_id', m.id);
                            setMerchantSearch('');
                            setMerchantDropdownOpen(false);
                          }}
                        >
                          {m.company_name} {m.shortcode ? `(${m.shortcode})` : ''}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {!formData.company_id && (
                <p className="text-xs text-orange-600 mt-1">A merchant must be selected to create a deal</p>
              )}
            </div>

            <div>
              <Label htmlFor="deal_shortcode">Deal Shortcode</Label>
              <Input
                id="deal_shortcode"
                value={formData.deal_shortcode}
                onChange={(e) => handleChange('deal_shortcode', e.target.value)}
                placeholder="Auto-generated from merchant"
              />
            </div>

            <div>
              <Label htmlFor="deal_id">Deal ID *</Label>
              <Input
                id="deal_id"
                type="text"
                value={formData.deal_id}
                onChange={(e) => handleChange('deal_id', e.target.value)}
                placeholder="e.g. 10604 or E201"
                required
              />
            </div>

            <div>
              <Label htmlFor="deal_name">Deal Name *</Label>
              <Input
                id="deal_name"
                value={formData.deal_name}
                onChange={(e) => handleChange('deal_name', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="funding_date">Funding Date *</Label>
              <Input
                id="funding_date"
                type="date"
                value={formData.funding_date}
                onChange={(e) => handleChange('funding_date', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="funding_amount">Funding Amount *</Label>
              <Input
                id="funding_amount"
                type="number"
                step="0.01"
                value={formData.funding_amount}
                onChange={(e) => handleChange('funding_amount', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="rate">Rate (Factor) *</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                value={formData.rate}
                onChange={(e) => handleChange('rate', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="term_days">Term (Days) *</Label>
              <Input
                id="term_days"
                type="number"
                value={formData.term_days}
                onChange={(e) => handleChange('term_days', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="fees">Fees</Label>
              <Input
                id="fees"
                type="number"
                step="0.01"
                value={formData.fees}
                onChange={(e) => handleChange('fees', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="payback">Payback (Calculated)</Label>
              <Input
                id="payback"
                type="number"
                step="0.01"
                value={formData.payback}
                readOnly
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label htmlFor="payment_amount">Daily Payment (Calculated)</Label>
              <Input
                id="payment_amount"
                type="number"
                step="0.01"
                value={formData.payment_amount}
                readOnly
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label htmlFor="deposit_amount">Net to Merchant (Auto-calculated)</Label>
              <Input
                id="deposit_amount"
                type="number"
                step="0.01"
                value={formData.deposit_amount}
                readOnly
                className="bg-green-50 font-semibold text-green-700"
              />
            </div>

            <div>
              <Label htmlFor="estimated_end_date">Estimated End Date</Label>
              <Input
                id="estimated_end_date"
                type="date"
                value={formData.estimated_end_date}
                readOnly
                className="bg-gray-50"
              />
            </div>

            <div className="flex items-end pb-1">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="refi"
                  data-testid="refi-checkbox"
                  checked={formData.refi}
                  onCheckedChange={(checked) => {
                    handleChange('refi', checked);
                    if (!checked) {
                      handleChange('refi_from_deal', '');
                      handleChange('refi_balance_payoff', '');
                      setRefiDealSearch('');
                    }
                  }}
                />
                <Label htmlFor="refi" className="font-medium cursor-pointer">This is a Refinance</Label>
              </div>
            </div>
          </div>

          {/* Collection Schedule Section */}
          <div className="border rounded-lg p-4 mt-4 bg-slate-50">
            <h3 className="font-semibold text-sm mb-3">Collection Schedule</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="collection_frequency">Frequency</Label>
                <Select
                  value={formData.collection_frequency || 'daily'}
                  onValueChange={(value) => handleChange('collection_frequency', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.collection_frequency === 'weekly' && (
                <div>
                  <Label htmlFor="collection_weekday">Weekday</Label>
                  <Select
                    value={formData.collection_weekday || 'monday'}
                    onValueChange={(value) => handleChange('collection_weekday', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monday">Monday</SelectItem>
                      <SelectItem value="tuesday">Tuesday</SelectItem>
                      <SelectItem value="wednesday">Wednesday</SelectItem>
                      <SelectItem value="thursday">Thursday</SelectItem>
                      <SelectItem value="friday">Friday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.collection_frequency === 'custom' && (
                <>
                  <div className="col-span-2">
                    <Label className="mb-2 block">Collection Days</Label>
                    <div className="flex flex-wrap gap-2">
                      {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((day) => (
                        <label key={day} className="flex items-center gap-1 text-sm">
                          <Checkbox
                            checked={(formData.collection_days || []).includes(day)}
                            onCheckedChange={(checked) => {
                              const days = formData.collection_days || [];
                              const newDays = checked 
                                ? [...days, day]
                                : days.filter(d => d !== day);
                              handleChange('collection_days', newDays);
                            }}
                          />
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="payments_per_week">Payments per Week</Label>
                    <Input
                      id="payments_per_week"
                      type="number"
                      min="1"
                      max="7"
                      value={formData.payments_per_week || ''}
                      onChange={(e) => handleChange('payments_per_week', e.target.value)}
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="scheduled_payment">Scheduled Payment Amount</Label>
                <Input
                  id="scheduled_payment"
                  type="number"
                  step="0.01"
                  value={formData.scheduled_payment || formData.payment_amount || ''}
                  onChange={(e) => handleChange('scheduled_payment', e.target.value)}
                  placeholder="Auto-calculated or override"
                />
                <p className="text-xs text-slate-500 mt-1">Leave blank to auto-calculate</p>
              </div>
            </div>
          </div>

          {/* Payback Override Section */}
          <div className="border rounded-lg p-4 mt-4 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <Checkbox
                id="payback_override"
                checked={formData.payback_override || false}
                onCheckedChange={(checked) => handleChange('payback_override', checked)}
              />
              <Label htmlFor="payback_override" className="font-semibold text-sm">Override Payback Amount</Label>
            </div>
            {formData.payback_override && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Calculated Payback</Label>
                  <div className="text-sm font-mono bg-white p-2 rounded border">
                    ${formatCurrency(parseFloat(formData.funding_amount || 0) * parseFloat(formData.rate || 1)).replace('$', '')}
                  </div>
                </div>
                <div>
                  <Label htmlFor="payback_manual">Manual Payback Target *</Label>
                  <Input
                    id="payback_manual"
                    type="number"
                    step="0.01"
                    value={formData.payback_manual || ''}
                    onChange={(e) => handleChange('payback_manual', e.target.value)}
                    className="border-amber-400"
                  />
                </div>
              </div>
            )}
            <p className="text-xs text-amber-700 mt-2">
              {formData.payback_override 
                ? 'Manual payback becomes the payoff target for this deal.' 
                : 'Payback is calculated as: Funding Amount × Factor Rate'}
            </p>
          </div>

          {/* Refinance Details (shown when checkbox is checked) */}
          {formData.refi && (
            <div className="border rounded-lg p-3 bg-blue-50 border-blue-200 mt-4">
              <Label className="text-sm font-medium text-blue-800 mb-2 block">
                Which deal is being refinanced into this one?
              </Label>
                <div ref={refiRef} className="relative">
                  {formData.refi_from_deal ? (
                    <div 
                      className="flex items-center justify-between border rounded-md px-3 py-2 text-sm bg-white cursor-pointer hover:bg-gray-50"
                      onClick={() => { setRefiDropdownOpen(true); setRefiDealSearch(''); }}
                      data-testid="refi-deal-selected"
                    >
                      <span>
                        {(() => {
                          const d = (allDeals || []).find(dl => dl.id === formData.refi_from_deal);
                          return d ? `#${d.deal_id} - ${d.deal_name || d.merchant_name || ''}` : formData.refi_from_deal;
                        })()}
                      </span>
                      <X className="h-4 w-4 text-gray-400 hover:text-gray-600" onClick={(e) => { e.stopPropagation(); handleChange('refi_from_deal', ''); }} />
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        data-testid="refi-deal-search-input"
                        placeholder="Search by deal ID, name, or merchant..."
                        value={refiDealSearch}
                        onChange={(e) => { setRefiDealSearch(e.target.value); setRefiDropdownOpen(true); }}
                        onFocus={() => setRefiDropdownOpen(true)}
                        className="pl-9 bg-white"
                        autoComplete="off"
                      />
                    </div>
                  )}
                  {refiDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredRefiDeals.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No deals found</div>
                      ) : (
                        filteredRefiDeals.map(d => (
                          <div
                            key={d.id}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex justify-between"
                            onClick={() => {
                              handleChange('refi_from_deal', d.id);
                              // 08/05: back-fill required contacts from the source
                              // deal — the backend 400s deals missing broker/funder,
                              // and those fields live on another tab so refis kept
                              // failing silently. Only fills what's still empty.
                              if (!formData.company_id && d.company_id) handleChange('company_id', d.company_id);
                              if (!formData.broker_id && d.broker_id) handleChange('broker_id', d.broker_id);
                              if (!formData.funder_id && d.funder_id) handleChange('funder_id', d.funder_id);
                              if (!formData.deal_shortcode && d.deal_shortcode) handleChange('deal_shortcode', d.deal_shortcode);
                              if (!formData.deal_name && (d.deal_name || d.merchant_name)) handleChange('deal_name', d.deal_name || d.merchant_name);
                              setRefiDealSearch('');
                              setRefiDropdownOpen(false);
                            }}
                          >
                            <span className="font-medium">#{d.deal_id} - {d.deal_name || d.merchant_name || ''}</span>
                            <span className="text-gray-400 text-xs">{d.status} | Bal: {formatCurrency(d.balance_with_fees ?? d.balance ?? 0)}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Refi Deal Balance & Payoff */}
                {formData.refi_from_deal && (
                  <div className="mt-3 space-y-3">
                    {refiLoading ? (
                      <p className="text-xs text-blue-600 animate-pulse">Loading deal balance...</p>
                    ) : refiDealDetail ? (
                      <>
                        <div className="bg-white rounded-md border border-blue-200 p-3 space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Balance Due:</span>
                            <span className="font-medium">{formatCurrency(refiDealDetail.balance ?? 0)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">+ Outstanding Fees:</span>
                            <span className="font-medium text-red-600">{formatCurrency(refiDealDetail.total_fees ?? 0)}</span>
                          </div>
                          <div className="border-t pt-1 flex justify-between text-sm font-semibold">
                            <span className="text-blue-800">Effective Balance (Payoff):</span>
                            <span className="text-blue-800">{formatCurrency(refiDealDetail.balance_with_fees ?? refiDealDetail.balance ?? 0)}</span>
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-blue-800">Payoff Amount</Label>
                          <p className="text-xs text-gray-500 mb-1">This amount will be deducted from the new deal's funding and paid to close the old deal.</p>
                          <Input
                            data-testid="refi-payoff-amount"
                            type="number"
                            step="0.01"
                            value={formData.refi_balance_payoff || ''}
                            onChange={(e) => handleChange('refi_balance_payoff', e.target.value)}
                            className="bg-white"
                          />
                        </div>

                        {formData.funding_amount && formData.refi_balance_payoff && (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-amber-800">
                                <Info className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                                Refinance Payoff Confirmation:
                              </span>
                              <span className="text-sm font-bold text-red-700">
                                -{formatCurrency(parseFloat(formData.refi_balance_payoff) || 0)}
                              </span>
                            </div>
                            <p className="text-xs text-amber-700">This amount will be deducted from the funding and applied as a REFINANCE_PAYOFF transaction to the selected deal.</p>
                            <div className="flex justify-between items-center pt-1 border-t border-amber-300">
                              <span className="text-sm font-medium text-green-800">
                                Net to Merchant after payoff:
                              </span>
                              <span className={`text-lg font-bold ${(parseFloat(formData.funding_amount) - parseFloat(formData.fees || 0) - parseFloat(formData.refi_balance_payoff)) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                                {formatCurrency((parseFloat(formData.funding_amount) || 0) - (parseFloat(formData.fees) || 0) - (parseFloat(formData.refi_balance_payoff) || 0))}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            )}
        </TabsContent>

        {/* Tab 2: Participants */}
        <TabsContent value="participants" className="space-y-4">
          <ParticipantsSection formData={formData} handleChange={handleChange} brokers={brokers} funders={funders} SearchableCombobox={SearchableCombobox} getBrokerLabel={getBrokerLabel} getFunderLabel={getFunderLabel} />
        </TabsContent>

        {/* Tab 3: Business Information */}
        <TabsContent value="business-info" className="space-y-4">
          <BusinessInfoSection formData={formData} handleChange={handleChange} />
        </TabsContent>

        {/* Tab 4: Owner Information */}
        <TabsContent value="owner-info" className="space-y-4">
          <OwnerInfoSection formData={formData} handleChange={handleChange} />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {editingDeal ? 'Update Deal' : 'Create Deal'}
        </Button>
      </div>
    </form>
  );
});

export default DealForm;
